import {Injectable} from '@angular/core';
import {Store} from '@ngxs/store';
import {BlackboardAction, BlackboardState, BlackboardStateModel} from '@app/state/blackboard.state';
import {
  ClassroomMessage,
  DataService,
  MessageRecord,
  MsgRecordType,
  RecordArgs,
  RecordStack
} from '@app/services/data.service';
import {MessageHandler} from '@app/services/message-handler';
import {LocalUser, LocalUserState} from '@app/state/local-user.state';
import {Observable} from 'rxjs';
import {BlackboardData, Coordinates} from '@app/modules/blackboard/model/blackboardInfo';
import {HttpClient} from '@angular/common/http';
import {ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {BlackboardCommands, BlackboardEvents, BlackboardStates} from '@app/models/commands/blackboard-commands';
import {WebSocketService} from '@app/modules/shared/services/web-socket.service';
import {ModuleService} from '@app/services/module-service';
import {map} from 'rxjs/operators';


@Injectable({
  providedIn: 'root'
})
export class BlackboardService implements MessageHandler, ModuleService {
  private dataService: DataService;
  private localUser: LocalUser;

  private classroom: ClassroomStateModel;
  private blackboard: BlackboardStateModel;
  private context: CanvasRenderingContext2D;
  private canvas: HTMLCanvasElement;
  private pointerElement: SVGSVGElement;

  public toggleBoard: (send: boolean) => void;
  public turnOnPointer: (send: boolean) => void;
  public turnOffPointer: (send: boolean) => void;
  public setText: (text: string) => void;

  public handleRecordStack: (recordStack: RecordStack) => Promise<void>; /* should be set in BlackboardCommands */

  constructor(
    private http: HttpClient,
    private webSocketService: WebSocketService,
    private store: Store
  ) {
    // !!! LAZY INIT OBJECTS (must NOT be used in constructor):
    // - this.dataService
    this.listenStoreChanges();
  }

  public init(): void {
    this.getState().subscribe();
  }

  public getState(): Observable<any> {
    /* clear blackboard */
    this.store.dispatch(new BlackboardAction.CleanAllBB(true));
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.setText('');

    return this.http.get(`${this.classroom.vcrURL}/blackboard/get-state`)
      .pipe(map((result: any) => {
        console.log(`GET STATE - ${result.message}`);
        this.handleRecordStack(result.data as RecordStack).then();
      }));
  }


  public sendToggle(show: boolean): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        BlackboardStates.TOGGLE_BOARD,
        new RecordArgs(show + '')
      )
    };
    return this.send(`${this.classroom.vcrURL}/blackboard/t`, message);
  }
  public onToggleBoard(record: MessageRecord): void {
    const open: boolean = record.data === 'true';
    this.store.dispatch(new BlackboardAction.ToggleBBOnOff(open));
    this.toggleBoard(false);
  }

  public sendTogglePointer(show: boolean): Observable<any> {
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        BlackboardStates.TOGGLE_POINTER,
        new RecordArgs(show + '')
      )
    };
    return this.send(`${this.classroom.vcrURL}/blackboard/t`, message);
  }
  public onTogglePointer(record: MessageRecord): void {
    const show: boolean = record.data === 'true';
    this.store.dispatch(new BlackboardAction.ToggleBBPointer(show));
    if (show) {
      this.turnOnPointer(false);
    } else {
      this.turnOffPointer(false);
    }
  }

  public sendPointerMove(coordinates: Coordinates): void {
    const recordData: string = JSON.stringify({coordinates});
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        BlackboardEvents.POINTER_MOVE,
        new RecordArgs(recordData)
      )
    };
    this.webSocketService.send('/app/t-bb', JSON.stringify(message));
  }
  public onPointerMove(record: MessageRecord): void {
    const coordinates: Coordinates = JSON.parse(record.data).coordinates;
    this.movePointer(coordinates, false);
  }

  public sendClear(): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        BlackboardEvents.CLEAR,
        new RecordArgs('')
      )
    };
    return this.send(`${this.classroom.vcrURL}/blackboard/purge`, message);
  }
  public onClear(record: MessageRecord): void {
    // record is supposed to be null - no additional data needed
    this.store.dispatch(new BlackboardAction.CleanAllBB(true));
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.setText('');
  }

  public sendDraw(blackboardData: BlackboardData): Observable<any> {
    const recordData: string = JSON.stringify({blackboardData});
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        BlackboardEvents.DRAW,
        new RecordArgs(recordData)
      )
    };
    return this.send(`${this.classroom.vcrURL}/blackboard/t`, message);
  }
  public onDraw(record: MessageRecord): void {
    const blackboardData: any = JSON.parse(record.data).blackboardData.bbDrawInfo;
    this.store.dispatch(new BlackboardAction.SetData(blackboardData));

    if (blackboardData.xyArray.length > 0) {
      for (let i = 0; i < blackboardData.xyArray.length; i++) {
        this.context.beginPath();
        if (i > 0) {
          this.context.moveTo(blackboardData.xyArray[i - 1].x, blackboardData.xyArray[i - 1].y);
        }
        this.context.strokeStyle = blackboardData.color;
        this.context.lineWidth = blackboardData.lineWide;
        this.context.lineTo(blackboardData.xyArray[i].x, blackboardData.xyArray[i].y);
        this.context.stroke();
      }
    }
  }

  public sendText(text: string): Observable<any> {
    const recordData: any = JSON.stringify({text});
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      record: this.buildRecord(
        BlackboardEvents.TEXT,
        new RecordArgs(recordData)
      )
    };
    return this.send(`${this.classroom.vcrURL}/blackboard/t`, message);
  }
  public onText(record: MessageRecord): void {
    const text: string = JSON.parse(record.data).text;
    this.store.dispatch(new BlackboardAction.BBTextHandler(text));
    this.setText(text);
  }

  public movePointer(coordinates: Coordinates, send: boolean = true): void {
    this.pointerElement.style.left = coordinates.x + 'px';
    this.pointerElement.style.top = coordinates.y + 'px';
    if (send) {
      this.sendPointerMove(coordinates);
    }
  };

  public buildRecord(command: string, args: RecordArgs): MessageRecord {
    const msgType = BlackboardStates[command] ? MsgRecordType.STATE : BlackboardEvents[command] ? MsgRecordType.EVENT : null;
    return {
      category: BlackboardCommands.category,
      name: command,
      data: args.data,
      type: msgType,
      ownerId: null, /* must be set server-side on receive */
      targetId: args.targetId
    };
  }

  public initCanvas(canvas: HTMLCanvasElement, context: CanvasRenderingContext2D, pointer: SVGSVGElement): void {
    this.canvas = canvas;
    this.context = context;
    this.pointerElement = pointer;
  }

  public alertTextBorderOut(): void {
    const blackboardText = document.getElementById('bbTextArea');
    blackboardText.style.height = 'auto';
    if (blackboardText.scrollHeight > this.canvas.offsetHeight + 2) {
      blackboardText.style.height = blackboardText.scrollHeight + 'px';
      blackboardText.style.color = '#fa0202';
    } else {
      blackboardText.style.height = this.canvas.offsetHeight + 'px';
      blackboardText.style.color = '#FFFFFF';
    }
  }

  public setDataService(dataService: DataService): void {
    this.dataService = dataService;
  }

  private listenStoreChanges() {
    this.store.select(ClassroomState).subscribe((classroom: ClassroomStateModel) => {
      this.classroom = classroom;
    });
    this.store.select(BlackboardState).subscribe((blackboard: BlackboardStateModel) => {
      this.blackboard = blackboard;
    });
    this.store.select(LocalUserState).subscribe((localUser: LocalUser) => {
      this.localUser = localUser;
    });
  }

  private send(destination: string, data: object, options?: object): Observable<object> {
    return this.http.post(destination, data, options);
  }
}
